import React, { useRef, useState } from "react";

import { Button, CircularProgress, Grid, Typography } from "@mui/material";
import {
  Add,
  BlindsClosedOutlined,
  DoorFrontOutlined,
  ElevatorOutlined,
  SolarPowerOutlined,
} from "@mui/icons-material";

import { BlockStandard } from "components/admin/BlockStandard";
import { PageStandard } from "components/admin/PageStandard";
import { createBuilding } from "api/onboarding";
import { useToken, useUserContext } from "contexts/UserContext";
import {
  CREATE_BLOCKS,
  CREATE_ENVIRONMENT,
  CREATE_INSTALATION,
  CREATE_PHOTOVOLTAIC,
} from "routers/routePaths";
import { ButtonLink } from "components/MUIExtensions/ButtonLink";
import { useSetError, useErrorInfo } from "contexts/ErrorContext";
import { CustomCodes, ErrorResponse } from "contexts/ErrorContextData";
import { FileUpload } from "components/MUIExtensions/FileUpload";
import { BuildingDetail } from "components/forms/BuildingDetail";

export const BuildingCreate = () => {
  const { currentSpace } = useUserContext();
  const setError = useSetError();
  const errorInfo = useErrorInfo();
  const token = useToken();
  const infoRef = useRef();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSaved, setHasSaved] = useState<boolean>(false);
  const [newImg, setNewImg] = useState<HTMLInputElement["files"] | null>(null);

  const handleSubmit = () => {
    // @ts-expect-error custom ref functions are not recognized by TS
    const isFormValid = infoRef.current?.isFormValid() && !!newImg?.length;
    if (isFormValid) {
      // @ts-expect-error custom ref functions are not recognized by TS
      const formValues = infoRef.current?.getFormValues();
      formValues["buildingImage"] = newImg[0];
      handleCreate(formValues);
    } else {
      setError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  const handleCreate = async (formValues) => {
    setIsLoading(true);
    try {
      await createBuilding(token, currentSpace?.id, formValues);
      setHasSaved(true);
    } catch (error) {
      setError(error as ErrorResponse);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = (file) => {
    if (file) {
      setNewImg(file);
    } else {
      setError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  return (
    <PageStandard title="Cadastro do imóvel">
      <BlockStandard>
        <FileUpload
          fullWidth
          currentFile={newImg}
          text="Alterar imagem"
          onFileUpload={handleFileUpload}
          hasError={errorInfo && !newImg?.length}
        />
      </BlockStandard>

      <BlockStandard>
        <BuildingDetail ref={infoRef} />
      </BlockStandard>

      <BlockStandard>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <ElevatorOutlined sx={{ mr: 1 }} />
          <Typography sx={{ flex: 2 }} variant="h3">
            Blocos e Pavimentos
          </Typography>
          <ButtonLink
            disabled={!hasSaved}
            startIcon={<Add />}
            sx={{ flex: 2 }}
            variant="contained"
            to={CREATE_BLOCKS}
          >
            Configurar
          </ButtonLink>
        </Grid>
      </BlockStandard>

      <BlockStandard>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <BlindsClosedOutlined />
          <Typography sx={{ flex: 2, ml: 1 }} variant="h3">
            Instalações
          </Typography>
          <ButtonLink
            disabled={!hasSaved}
            startIcon={<Add />}
            sx={{ flex: 2 }}
            variant="contained"
            to={CREATE_INSTALATION}
          >
            Nova instalação
          </ButtonLink>
        </Grid>
      </BlockStandard>

      <BlockStandard>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <SolarPowerOutlined />
          <Typography sx={{ flex: 2, ml: 1 }} variant="h3">
            Energia solar
          </Typography>
          <ButtonLink
            disabled={!hasSaved}
            startIcon={<Add />}
            sx={{ flex: 2 }}
            variant="contained"
            to={CREATE_PHOTOVOLTAIC}
          >
            Contratar
          </ButtonLink>
        </Grid>
      </BlockStandard>

      <BlockStandard hideDivider>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <DoorFrontOutlined sx={{ mr: 1 }} />
          <Typography sx={{ flex: 2 }} variant="h3">
            Ambientes
          </Typography>
          <ButtonLink
            disabled={!hasSaved}
            startIcon={<Add />}
            sx={{ flex: 2 }}
            variant="contained"
            to={CREATE_ENVIRONMENT}
          >
            Novo Ambiente
          </ButtonLink>
        </Grid>
      </BlockStandard>

      <BlockStandard hideDivider>
        <Button variant="contained" onClick={handleSubmit} disabled={isLoading}>
          {!isLoading ? "Salvar" : <CircularProgress />}
        </Button>
      </BlockStandard>
    </PageStandard>
  );
};
